import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Plus, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

interface ObservacionInput {
  campo: string;
  documentoId: string;
  observacion: string;
}

/**
 * Modal de Calidad para observar un RL en revisión. Construye la lista de
 * observaciones (campo / documentoId / observación) y dispara
 * POST /api/registros-legales/{id}/observar. El backend hace append al
 * array observaciones_qa y mueve a estatus OBSERVADO.
 */
export function ObservarRLModal({
  open,
  rlId,
  onClose,
  onDone,
}: {
  open: boolean;
  rlId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [items, setItems] = useState<ObservacionInput[]>([
    { campo: "", documentoId: "", observacion: "" },
  ]);
  const [error, setError] = useState<string | null>(null);

  const observar = useMutation({
    mutationFn: async () => {
      const body = {
        observaciones: items
          .filter((i) => i.observacion.trim().length > 0)
          .map((i) => ({
            campo: i.campo.trim() || null,
            documentoId: i.documentoId.trim() || null,
            observacion: i.observacion.trim(),
          })),
      };
      if (body.observaciones.length === 0) {
        throw new Error("Agrega al menos una observación con texto.");
      }
      await api.post(`/api/registros-legales/${rlId}/observar`, body);
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } }; message?: string };
      setError(r.response?.data?.detail || r.response?.data?.title || r.message || "Error");
    },
  });

  if (!open) return null;

  const setItem = (i: number, patch: Partial<ObservacionInput>) =>
    setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addItem = () => setItems((arr) => [...arr, { campo: "", documentoId: "", observacion: "" }]);
  const delItem = (i: number) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl overflow-hidden rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">Observar Registro Legal</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-surface-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="text-sm text-fg-muted">
            Captura las observaciones que el cliente debe atender. Cada observación puede apuntar a un
            <strong> campo</strong> específico (p.ej. <code>razonSocial</code>), a un{" "}
            <strong>documento</strong> (UUID del doc) o ser global (deja ambos vacíos).
          </p>

          {items.map((it, i) => (
            <div key={i} className="rounded border p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-medium text-fg-subtle">Observación #{i + 1}</div>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => delItem(i)}
                    className="rounded p-1 text-estado-err hover:bg-estado-err-bg"
                    aria-label="Eliminar observación"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label="Campo (opcional)">
                  <input
                    type="text"
                    value={it.campo}
                    onChange={(e) => setItem(i, { campo: e.target.value })}
                    placeholder="razonSocial, rfc, domicilio…"
                    className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                  />
                </Field>
                <Field label="Documento ID (opcional)">
                  <input
                    type="text"
                    value={it.documentoId}
                    onChange={(e) => setItem(i, { documentoId: e.target.value })}
                    placeholder="UUID del documento"
                    className="w-full rounded border px-2 py-1.5 font-mono text-xs"
                  />
                </Field>
              </div>
              <Field label="Observación *">
                <textarea
                  rows={2}
                  required
                  value={it.observacion}
                  onChange={(e) => setItem(i, { observacion: e.target.value })}
                  className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
                />
              </Field>
            </div>
          ))}

          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
          >
            <Plus className="h-4 w-4" />
            Agregar otra observación
          </button>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-surface-2 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={observar.isPending}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => observar.mutate()}
            disabled={observar.isPending}
            className="rounded bg-action-warn px-4 py-1.5 text-sm font-medium text-white transition hover:bg-action-warn-hover disabled:opacity-50"
          >
            {observar.isPending ? "Enviando…" : "Devolver al cliente para subsanar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      {children}
    </div>
  );
}
